import { FC } from 'react';
import { BoxV2 as Box, Flex } from 'portal-commons';

import { CampaignDetail } from '@/apis/core/types';
import { convertTimeWithTimezone } from '@/utils/time';
import { humanize } from '@/utils/string';

interface CampaignSummaryProps {
  campaign: CampaignDetail;
  usecaseMap?: Record<string, string>;
}

const Field = ({
  label,
  'data-testid': testId,
  children,
}: {
  label: string;
  'data-testid'?: string;
  children: React.ReactNode;
}) => {
  return (
    <Flex
      sx={{
        fontSize: 'H400',
        fontWeight: 'normal',
        color: 't.black80',
        lineHeight: 'normal',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}
      data-testid={testId}
    >
      <label>{label}:</label>
      <Box
        as="span"
        sx={{ ml: '4px', fontWeight: 'semiBold', wordBreak: 'break-word' }}
      >
        {children ?? 'N/A'}
      </Box>
    </Flex>
  );
};

const CampaignSummary: FC<CampaignSummaryProps> = ({
  campaign,
  usecaseMap,
}) => {
  return (
    <Box sx={{ mb: 'xxxxl' }}>
      <Box
        as="h1"
        sx={{
          mb: '32px',
          fontSize: 'H600',
          fontWeight: 400,
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        RCS Campaign ID:
        <Box
          as="span"
          sx={{ fontWeight: 'semiBold', ml: 'xs' }}
          data-testid="campaignId"
        >
          {campaign?.uid}
        </Box>
      </Box>

      <Flex
        sx={{
          mb: 'xs',
          color: 't.black60',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: 's',
            justifyContent: 'flex-end',
            width: 'fit-content',
            maxWidth: 'calc(50% - 78px)',
          }}
        >
          <Field label="Created On" data-testid="campaignCreatedOn">
            {convertTimeWithTimezone(
              campaign.createDate,
              undefined,
              'MM/DD/YYYY'
            )}
          </Field>
          <Field label="Agent ID" data-testid="campaignAgentId">
            {campaign.googleAgentId}
          </Field>
        </Flex>
        <Box
          as="span"
          role="divider"
          sx={{
            width: '2px',
            backgroundColor: '#EDEEEF',
            height: '70px',
            mx: '77px',
          }}
        />
        <Flex
          sx={{
            flexDirection: 'column',
            gap: 's',
            width: 'fit-content',
            maxWidth: 'calc(50% - 78px)',
          }}
        >
          <Field label="Use-Case" data-testid="campaignUseCase">
            {campaign?.usecase &&
              (usecaseMap?.[campaign.usecase] ?? campaign.usecase)}
          </Field>
          <Field label="Billing Category" data-testid="campaignBillingCategory">
            {campaign.billingCategory && humanize(campaign.billingCategory)}
          </Field>
        </Flex>
      </Flex>
    </Box>
  );
};

export default CampaignSummary;
